import React, { useRef, useState } from "react";
import { subYears } from "date-fns";
import { Trans, useWebTranslation } from "translations";
import ChatMessage from "ui-kit/ChatMessage/ChatMessage";
import { StackedTextInput } from "ui-kit/TextInput/TextInput";
import { useSignupDispatch } from "components/Signup/store/hooks";
import { updateCustomer } from "components/Signup/store/slices/customer";
import { validateEmail, validatePhone } from "utils/validation";
import { StackedDateInput } from "components/DateInput/DateInput";
import Gutter from "ui-kit/Gutter";
import { Typography } from "ui-kit/Typography";
import { useCustomer } from "components/Signup/store/selectors/customer";
import Colors from "ui-kit/colors";
import AcceptBox from "../AcceptBoxFra/AcceptBox";

export const UserInfoForm = () => {
  const { t } = useWebTranslation();
  const [emailValid, setEmailValid] = useState(true);
  const [phoneValid, setPhoneValid] = useState(true);
  const [dateValid, setDateValid] = useState(true);
  const [dateValidMin, setDateValidMin] = useState(true);
  const [dateValidMax, setDateValidMax] = useState(true);

  const minAge = subYears(new Date(), 120);
  const maxAge = subYears(new Date(), 18);

  const {
    firstName,
    lastName,
    email,
    phone,
    birthdate,
    address,
    marketingPermissionAccepted,
  } = useCustomer();

  const initialMarketingPermission = useRef(marketingPermissionAccepted);
  const initialEmail = useRef(email);
  const initialPhone = useRef(phone);
  const dispatch = useSignupDispatch();

  return (
    <>
      <ChatMessage fromMe unSent small border={false}>
        <StackedTextInput
          first
          placeHolder={t("global.first_name")}
          value={firstName ?? ""}
          onChangeText={(firstName) => dispatch(updateCustomer({ firstName }))}
          autoComplete="given-name"
        />
        <StackedTextInput
          placeHolder={t("global.last_name")}
          value={lastName ?? ""}
          onChangeText={(lastName) => dispatch(updateCustomer({ lastName }))}
          autoComplete="family-name"
        />
        {!initialEmail.current && (
          <StackedTextInput
            placeHolder={t("global.email")}
            value={email ?? ""}
            onChangeText={(email) => {
              if (!emailValid) {
                setEmailValid(validateEmail(email));
              }
              dispatch(updateCustomer({ email }));
            }}
            onBlur={(e) => {
              setEmailValid(validateEmail(e.target.value));
            }}
            name="email"
            autoComplete="email"
            type="email"
          />
        )}
        {!initialPhone.current && (
          <StackedTextInput
            type="tel"
            autoComplete="phone"
            placeHolder={t("global.phone")}
            value={phone ?? ""}
            onChangeText={(userPhone) => {
              const formattedPhone = userPhone.replace(/[^\d+-\s]+/g, "");

              if (!phoneValid) {
                setPhoneValid(validatePhone(formattedPhone));
              }
              dispatch(updateCustomer({ phone: formattedPhone }));
            }}
            onBlur={(e) => {
              setPhoneValid(validatePhone(e.target.value));
            }}
          />
        )}
        <StackedDateInput
          value={birthdate ?? ""}
          onChange={({ date, validation }) => {
            if (!dateValid) {
              setDateValid(validation.isValid);
            }
            setDateValidMin(validation.isValidMin);
            setDateValidMax(validation.isValidMax);

            dispatch(updateCustomer({ birthdate: date }));
          }}
          onBlur={({ validation }) => {
            setDateValid(validation.isValid);
          }}
          min={minAge}
          max={maxAge}
          noValidate
          autoComplete="bday"
        />
        <StackedTextInput
          placeHolder={t("signup.user_info.street_name_and_number")}
          value={address?.line1 ?? ""}
          onChangeText={(line1) => {
            dispatch(
              updateCustomer({
                address: {
                  line1,
                  city: address?.city ?? null,
                  zip: address?.zip ?? "",
                },
              })
            );
          }}
          autoComplete="street-address"
        />
        <StackedTextInput
          placeHolder={t("signup.user_info.address.zip")}
          value={address?.zip ?? ""}
          onChangeText={(zip) => {
            dispatch(
              updateCustomer({
                address: {
                  line1: address?.line1 ?? null,
                  city: address?.city ?? null,
                  zip: zip,
                },
              })
            );
          }}
          autoComplete="zip"
        />
        <StackedTextInput
          last
          placeHolder={t("signup.user_info.city")}
          value={address?.city ?? ""}
          onChangeText={(city) => {
            dispatch(
              updateCustomer({
                address: {
                  line1: address?.line1 ?? null,
                  city,
                  zip: address?.zip ?? "",
                },
              })
            );
          }}
          autoComplete="city"
        />
        <Gutter size={1} />
        {!emailValid && (
          <>
            <Gutter size={1} />
            <Typography color={"error"} testId="date-error">
              {t("validation.email")}
            </Typography>
            <Gutter size={1} />
          </>
        )}
        {!phoneValid && (
          <>
            <Gutter size={1} />
            <Typography color={"error"} testId="date-error">
              {t("validation.phone")}
            </Typography>
            <Gutter size={1} />
          </>
        )}
        {!(dateValid && dateValidMin) && (
          <>
            <Gutter size={1} />
            <Typography color={"error"} testId="date-error">
              {t("date_input.error")}
            </Typography>
            <Gutter size={1} />
          </>
        )}
        {!dateValidMax && (
          <>
            <Gutter size={1} />
            <Typography color={"error"}>
              {t("validation.underage_person")}
            </Typography>
          </>
        )}
        {!initialMarketingPermission.current && (
          <>
            <Gutter size={2} />
            <AcceptBox
              textKey="signup.user_info.marketing_notice.description"
              links={[]}
              selected={marketingPermissionAccepted}
              onSelect={() => {
                dispatch(
                  updateCustomer({
                    marketingPermissionAccepted: !marketingPermissionAccepted,
                  })
                );
              }}
            />
            <Gutter size={2} />
          </>
        )}

        <Typography
          variant="body3"
          color="grey500"
          style={{ display: "block", width: "100%", paddingLeft: 16 }}
        >
          <Trans
            i18nKey="signup.user_info.terms_notice.description"
            components={[
              "https://docs.lassie.co/fra/latest/cgu.pdf",
              "https://www.lassie.fr/politique-de-confidentialite",
            ].map((href) => (
              <a
                key={0}
                // className={styles.acceptBoxLink}
                href={href}
                style={{
                  textDecoration: "underline",
                  color: Colors.blue300,
                }}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              />
            ))}
          />
        </Typography>
      </ChatMessage>
    </>
  );
};

export default UserInfoForm;
